import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme,
  Avatar,
  Divider,
  ListItemIcon
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';
import DownloadApp from './DownloadApp';
import { AccountCircle as ProfileIcon, Logout as LogoutIcon, Settings as SettingsIcon } from '@mui/icons-material';

// Navigation items for the menu
const pages = [
  { title: 'Home', path: '/' },
  { title: 'Features', path: '/features' },
  { title: 'Pricing', path: '/pricing' },
  { title: 'About', path: '/about' },
  { title: 'Contact', path: '/contact' }
];

const Header: React.FC = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, logout, userProfile } = useAuth();

  // Extract user information for avatar display
  const userFirstName = userProfile?.firstName || currentUser?.email?.split('@')[0] || 'User';
  const userInitial = (userProfile?.firstName?.charAt(0) || currentUser?.email?.charAt(0) || 'U').toUpperCase();
  const profilePhotoURL = userProfile?.photoURL;

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleNavigate = (path: string) => {
    handleCloseNavMenu();
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleCloseUserMenu();
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleCloseUserMenu();
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    handleCloseUserMenu();
  };

  // Avatar with dropdown menu
  const renderAvatarWithDropdown = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {!isMobile && (
        <Typography variant="body1" sx={{ display: { xs: 'none', md: 'block' } }}>
          Hey there, {userFirstName}!
        </Typography>
      )}
      {isMobile && (
        <Typography variant="body2" sx={{ 
          fontWeight: 500,
          whiteSpace: 'nowrap'
        }}>
          Hey there, {userFirstName}!
        </Typography>
      )}
      <Box>
        <IconButton
          onClick={handleOpenUserMenu}
          sx={{ 
            p: 0,
            '&:hover': {
              bgcolor: 'transparent'
            }
          }}
        >
          <Avatar 
            src={profilePhotoURL} 
            alt="Profile" 
            sx={{ 
              width: isMobile ? 36 : 42, 
              height: isMobile ? 36 : 42,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              border: '2px solid',
              borderColor: 'primary.light',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.5)',
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            {userInitial}
          </Avatar>
        </IconButton>
        <Menu
          sx={{ mt: '45px' }}
          id="menu-appbar"
          anchorEl={anchorElUser}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
        >
          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <ProfileIcon fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={handleSettingsClick}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  // Render non-authenticated mobile header
  const renderMobileNonAuthHeader = () => (
    <Box sx={{ 
      display: 'flex', 
      width: '100%', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      position: 'relative'
    }}>
      {/* Empty space on left - removing login button */}
      <Box sx={{ width: 70 }} />
      
      {/* Centered logo for mobile */}
      <Box 
        sx={{ 
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => navigate('/')}
      >
        <Logo variant="full" size="small" />
      </Box>
      
      {/* Sign Up button on right */}
      <Button
        variant="contained"
        onClick={() => navigate('/register')}
        sx={{ 
          zIndex: 1
        }}
      >
        Sign Up
      </Button>
    </Box>
  );

  return (
    <AppBar 
      position="sticky" 
      color="default" 
      elevation={1} 
      sx={{ 
        top: 0,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        borderBottom: 1, 
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ py: 1 }}>
          {/* Mobile view top bar */}
          {currentUser && isMobile ? (
            <Box sx={{ 
              display: 'flex', 
              width: '100%', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              {/* Empty space for balance on left */}
              <Box sx={{ width: 36 }} />
              
              {/* Centered logo for mobile */}
              <Box 
                sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/')}
              >
                <Logo variant="full" size="small" />
              </Box>
              
              {/* User greeting and avatar on right */}
              {renderAvatarWithDropdown()}
            </Box>
          ) : (!currentUser && isMobile ? (
            renderMobileNonAuthHeader()
          ) : (
            /* Main toolbar content for desktop or authenticated */
            <Box sx={{ 
              display: 'flex', 
              width: '100%', 
              alignItems: 'center',
              position: 'relative'
            }}>
              {currentUser ? (
                <>
                  {/* Logo for authenticated desktop view */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      mr: 2,
                      cursor: 'pointer' 
                    }}
                    onClick={() => navigate('/')}
                  >
                    <Logo variant="full" size="medium" />
                  </Box>

                  {/* Desktop menu */}
                  <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                    {pages.map((page) => (
                      <Button
                        key={page.title}
                        component={RouterLink}
                        to={page.path}
                        sx={{ 
                          my: 2, 
                          color: 'text.primary', 
                          display: 'block', 
                          mx: 1,
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        {page.title}
                      </Button>
                    ))}
                  </Box>
                </>
              ) : (
                /* Non-authenticated desktop view with centered logo */
                <>
                  {/* Empty space on left - removing login button */}
                  <Box sx={{ width: 80 }} />
                  
                  {/* Centered logo */}
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate('/')}
                  >
                    <Logo variant="full" size="medium" />
                  </Box>
                </>
              )}

              {/* Action buttons for desktop or non-logged in users */}
              <Box sx={{ display: 'flex', ml: 'auto' }}>
                {currentUser ? (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <DownloadApp variant="button" showText={true} />
                    <Button 
                      variant="contained" 
                      color="primary"
                      component={RouterLink}
                      to={String(userProfile?.role) === 'Employee' ? '/employee-dashboard' : '/dashboard'}
                      sx={{ 
                        fontWeight: 500,
                        textTransform: 'none'
                      }}
                    >
                      {String(userProfile?.role) === 'Employee' ? 'My Dashboard' : 'Dashboard'}
                    </Button>
                    
                    {/* Desktop view avatar with dropdown */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', ml: 1 }}>
                      {renderAvatarWithDropdown()}
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Render only Sign Up on desktop */}
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/login')}
                      sx={{
                        mx: 1,
                        display: { xs: isMobile ? 'none' : 'inline-flex', md: 'inline-flex' }
                      }}
                    >
                      Log In
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/register')}
                    >
                      Sign Up
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          ))}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header; 